import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import '../App.css';

const { FiX, FiChevronDown, FiHelpCircle, FiChevronRight, FiArrowRight } = FiIcons;

const AccordionItem = ({ title, children, isOpen, onClick }) => (
  <div className="border-b border-stone-100">
    <button
      className="w-full py-4 flex justify-between items-center text-left hover:text-moss-700 transition-colors group"
      onClick={onClick}
    >
      <span className="text-sm font-medium text-stone-700 group-hover:text-moss-800">{title}</span>
      <SafeIcon
        icon={FiChevronDown}
        className={`w-4 h-4 text-stone-300 transition-transform duration-500 ${isOpen ? 'rotate-180 text-moss-600' : ''}`}
      />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-4 text-sm text-stone-600 font-light leading-relaxed space-y-3 pr-8">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const LeafButton = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.08 }}
    whileTap={{ scale: 0.95 }}
    className="fixed bottom-8 right-8 z-30 flex flex-col items-center group"
  >
    <div className="relative">
      <svg width="120" height="120" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
        <path d="M50 90C50 90 50 60 20 40C5 25 30 10 50 10C70 10 95 25 80 40C50 60 50 90 50 90Z" fill="#79836a" />
        <path d="M50 15V85" stroke="white" strokeWidth="0.5" strokeOpacity="0.3" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[11px] text-white font-medium uppercase tracking-tighter text-center leading-tight px-3">
          Was<br/>erwartet<br/>mich?
        </span>
      </div>
    </div>
  </motion.button>
);

const BranchGraphic = () => (
  <svg width="120" height="auto" viewBox="0 0 120 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-20">
    <path d="M60 150C60 150 60 100 40 70C20 40 60 10 60 10" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <path d="M60 120C75 110 90 115 100 130" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <path d="M48 85C35 80 25 85 15 100" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <path d="M60 45C75 35 95 40 105 55" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

const DotPattern = () => (
  <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
    <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="2" fill="#79836a" />
      <circle cx="50" cy="20" r="2" fill="#79836a" />
      <circle cx="80" cy="20" r="2" fill="#79836a" />
      <circle cx="20" cy="50" r="2" fill="#79836a" />
      <circle cx="50" cy="50" r="2" fill="#79836a" />
      <circle cx="80" cy="50" r="2" fill="#79836a" />
      <circle cx="20" cy="80" r="2" fill="#79836a" />
      <circle cx="50" cy="80" r="2" fill="#79836a" />
      <circle cx="80" cy="80" r="2" fill="#79836a" />
    </svg>
  </div>
);

function Home() {
  const [activeModal, setActiveModal] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const openModal = (modal) => setActiveModal(modal);
  const closeModal = () => setActiveModal(null);

  return (
    <div className="min-h-screen bg-white text-stone-800 selection:bg-moss-100 selection:text-moss-900">
      <LeafButton onClick={() => openModal('expectations')} />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-40 border-b border-stone-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-xl font-light tracking-tight text-stone-900 flex items-center gap-3">
            <img src="https://github.com/mike3011/Bilder/blob/main/Logo%20MindyourNature.png?raw=true" alt="Mind Your Nature Logo" className="h-8 w-8" />
            Mind Your Nature
          </h1>
          <div className="flex items-center gap-4">
            <motion.a
              href="https://www.eventbrite.de/o/mind-your-nature-120905315027"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="hidden md:flex items-center gap-2 bg-[#79836a] hover:bg-[#6a7359] text-white text-xs font-light px-4 py-2 rounded-full transition-colors"
            >
              Buchen
              <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
            </motion.a>
            <button
              onClick={() => openModal('reflection')}
              className="text-[10px] uppercase tracking-[0.2em] text-stone-400 hover:text-moss-700 flex items-center gap-2 border border-stone-100 px-4 py-2 rounded-full transition-all hover:bg-stone-50"
            >
              <SafeIcon icon={FiHelpCircle} className="w-3 h-3" />
              Warum bin ich hier?
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 pt-32 pb-20">

        {/* Hero Section */}
        <section className="py-24 md:py-40 relative">
          <div className="absolute -top-20 -right-20 w-40 h-40 opacity-5">
            <BranchGraphic />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h2 className="text-4xl md:text-6xl font-light tracking-tight text-stone-900 mb-8 leading-[1.1]">
              den Autopiloten erkennen – <br className="hidden md:block" />
              <span className="text-moss-500/60 italic">Wahlfreiheit gewinnen</span>
            </h2>
            <p className="text-xl text-stone-500 font-light max-w-2xl leading-relaxed">
              Ein mehrtägiges Erfahrungsformat zur Wahrnehmung innerer Automatismen. <br />
              In der Natur – weil sie uns dabei natürlich unterstützt.
            </p>
          </motion.div>
        </section>

        {/* Praxis & Einordnung */}
        <section className="py-20 border-t border-stone-50">
          <div className="mb-16">
            <p className="text-moss-700 font-light text-lg italic border-l-2 border-moss-200 pl-6 py-2">
              „Wir arbeiten mit einer einfachen Praxis: <br className="md:hidden" />
              Innehalten – Wahrnehmen – Unterscheiden – Entscheiden."
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-6">
              <h3 className="text-[10px] uppercase tracking-[0.3em] text-stone-400">Einordnung</h3>
              <div className="space-y-4 font-light leading-relaxed text-stone-600">
                <p>Erkenne deine gewohnheitsmäßigen Reaktionen.</p>
                <p>Finde Spielraum zwischen Reiz und Reaktion.</p>
                <p>
                  Mind Your Nature bietet einen Raum, in dem das eigene Erleben wertfrei beobachtet werden kann.
                  Es gibt kein festgeschriebenes Ziel – die reine Wahrnehmung steht im Vordergrund.
                </p>
              </div>
            </div>

            <div className="bg-stone-50/50 p-10 rounded-sm border border-stone-100 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 text-moss-200 group-hover:text-moss-300 transition-colors">
                <BranchGraphic />
              </div>
              <h3 className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-8 relative z-10">Ein geschützter Raum</h3>
              <ul className="space-y-6 font-light text-stone-700 relative z-10">
                <li className="flex gap-4">
                  <span className="text-moss-300 font-medium">01</span>
                  <span>Wie nehme ich meine automatischen Reaktionen wahr?</span>
                </li>
                <li className="flex gap-4">
                  <span className="text-moss-300 font-medium">02</span>
                  <span>Wo liegt die Pause zwischen Reiz und Reaktion?</span>
                </li>
                <li className="flex gap-4">
                  <span className="text-moss-300 font-medium">03</span>
                  <span>Welcher Raum entsteht, wenn ich nicht sofort reagiere?</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Nutzen */}
        <section className="py-24 border-t border-stone-50 relative">
          <DotPattern />
          <h3 className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-12 relative z-10">Erfahrungswerte</h3>
          <div className="grid md:grid-cols-3 gap-12 text-stone-700 font-light relative z-10">
            <div className="space-y-3">
              <p className="text-moss-800 font-medium">Gedanken als Angebote</p>
              <p className="text-sm leading-relaxed text-stone-500">Das Erkennen von Gedanken als bloße Vorschläge, denen keine Handlung folgen muss.</p>
            </div>
            <div className="space-y-3">
              <p className="text-moss-800 font-medium">Stress-Dynamik sehen</p>
              <p className="text-sm leading-relaxed text-stone-500">Ein bewussteres Zeuge-Sein der eigenen Impulse in fordernden Momenten.</p>
            </div>
            <div className="space-y-3">
              <p className="text-moss-800 font-medium">Leichter zurück zur Ruhe</p>
              <p className="text-sm leading-relaxed text-stone-500">Ein natürlicheres Finden zurück ins Gleichgewicht nach Belastungsphasen.</p>
            </div>
          </div>
        </section>

        {/* Zielgruppen / Passung */}
        <section className="py-24 border-t border-stone-50">
          <h2 className="text-2xl font-light mb-16 text-stone-900 italic">Passung</h2>
          <div className="grid md:grid-cols-2 gap-20">
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-8">Dieser Raum ist für Menschen, die:</h4>
              <ul className="space-y-5 font-light text-stone-700">
                <li className="flex gap-3 items-start"><SafeIcon icon={FiChevronRight} className="mt-1 text-moss-400" /> <span>Oft über Situationen grübeln, die sie nicht ändern können</span></li>
                <li className="flex gap-3 items-start"><SafeIcon icon={FiChevronRight} className="mt-1 text-moss-400" /> <span>Merken, dass sie schnell gestresst oder gereizt reagieren</span></li>
                <li className="flex gap-3 items-start"><SafeIcon icon={FiChevronRight} className="mt-1 text-moss-400" /> <span>Sich manchmal wie fremdbestimmt fühlen</span></li>
                <li className="flex gap-3 items-start"><SafeIcon icon={FiChevronRight} className="mt-1 text-moss-400" /> <span>Mehr Gelassenheit im Alltag wollen</span></li>
              </ul>
            </div>

            <div className="opacity-60">
              <h4 className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-8">Nicht geeignet für Menschen:</h4>
              <ul className="space-y-5 font-light text-stone-500">
                <li className="flex gap-3 items-start"><span className="text-stone-300">—</span> <span>Mit Coaching- oder Therapieerwartung</span></li>
                <li className="flex gap-3 items-start"><span className="text-stone-300">—</span> <span>Die emotionale Aufarbeitung oder therapeutische Prozessarbeit erwarten</span></li>
                <li className="flex gap-3 items-start"><span className="text-stone-300">—</span> <span>In akuten persönlichen Krisen</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ - Accordion */}
        <section className="py-24 border-t border-stone-50">
          <h2 className="text-2xl font-light mb-8 text-stone-900">Häufige Fragen</h2>
          <div className="space-y-0">
            <AccordionItem title='Was ist „Mind Your Nature"?' isOpen={openFaq === 0} onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}>
              Mind Your Nature ist ein mehrtägiges Erfahrungsformat in der Natur. Es bietet einen klaren, reizarmen Rahmen, um eigene Gedanken, Gefühle und Handlungsimpulse bewusster wahrzunehmen. Ziel ist nicht Veränderung durch Anleitung, sondern mehr Klarheit im eigenen Erleben.
            </AccordionItem>
            <AccordionItem title="Ist das Coaching, Therapie oder ein Trainingsprogramm?" isOpen={openFaq === 1} onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}>
              Nein. Mind Your Nature ist weder Coaching noch Therapie und ersetzt keine medizinische oder psychologische Begleitung. Es gibt keine Beratung, keine Diagnosen, keine Methodenvermittlung und keine inhaltliche Führung.
            </AccordionItem>
            <AccordionItem title="Bekomme ich Input, Erklärungen oder Übungen an die Hand?" isOpen={openFaq === 2} onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}>
              Nein. Es gibt keine Belehrungen, keine Modelle und keine gedanklichen Konzepte, die „verstanden" werden sollen. Der Fokus liegt auf dem eigenen Erleben, nicht auf Wissen oder Interpretation.
            </AccordionItem>
            <AccordionItem title="Muss ich mich verändern oder an mir arbeiten?" isOpen={openFaq === 3} onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}>
              Nein. Es geht nicht darum, besser, ruhiger oder optimierter zu werden. Es geht darum, wahrzunehmen, was bereits da ist – ohne es sofort zu bewerten oder zu verändern.
            </AccordionItem>
            <AccordionItem title="Wie läuft der Austausch in der Gruppe ab?" isOpen={openFaq === 4} onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}>
              Austausch ist freiwillig. Wenn geteilt wird, dann sachlich und beobachtend, nicht problemorientiert oder emotional aufarbeitend. Rückzug und Alleinsein sind jederzeit gleichwertig.
            </AccordionItem>
            <AccordionItem title="Für wen ist das Format geeignet?" isOpen={openFaq === 5} onClick={() => setOpenFaq(openFaq === 5 ? null : 5)}>
              Für Menschen, die innere Unruhe oder Anspannung bemerken und ihre Reaktionen klarer wahrnehmen möchten – ohne Anleitung, ohne spirituellen Rahmen und ohne Optimierungsdruck.
            </AccordionItem>
            <AccordionItem title="Für wen ist das Format nicht geeignet?" isOpen={openFaq === 6} onClick={() => setOpenFaq(openFaq === 6 ? null : 6)}>
              Nicht geeignet ist das Format bei akuten persönlichen Krisen oder wenn Coaching, Therapie, Heilung oder konkrete Ergebnisse erwartet werden.
            </AccordionItem>
            <AccordionItem title="Wie buche ich die Teilnahme?" isOpen={openFaq === 7} onClick={() => setOpenFaq(openFaq === 7 ? null : 7)}>
              Die Teilnahme an Mind Your Nature buchst du über die angegebene Plattform (z. B. Eventbrite oder verlinkte Partner). Mit der Buchung sicherst du dir deinen Platz im Format.
            </AccordionItem>
            <AccordionItem title="Wie ist das mit Unterkunft und Übernachtung geregelt?" isOpen={openFaq === 8} onClick={() => setOpenFaq(openFaq === 8 ? null : 8)}>
              Die Unterkunft wird nicht über Mind Your Nature gebucht. Du buchst deine Übernachtung eigenständig direkt bei der angegebenen Location. So bleibt die Verantwortung bewusst bei dir.
            </AccordionItem>
            <AccordionItem title="Wie ist die Verpflegung organisiert?" isOpen={openFaq === 9} onClick={() => setOpenFaq(openFaq === 9 ? null : 9)}>
              Die Verpflegung wird nicht zentral organisiert. Jede Person kümmert sich eigenverantwortlich um das, was sie essen möchte. Wir streben an, gemeinsam zu kochen und zu essen, sofern das für die Gruppe stimmig ist.
            </AccordionItem>
            <AccordionItem title="Warum ist das so organisiert?" isOpen={openFaq === 10} onClick={() => setOpenFaq(openFaq === 10 ? null : 10)}>
              Die Trennung von Format, Unterkunft und Verpflegung ist bewusst gewählt. Sie unterstützt Eigenverantwortung und verhindert Versorgungs- oder Erwartungsrollen.
            </AccordionItem>
            <AccordionItem title="Was muss ich also konkret beachten?" isOpen={openFaq === 11} onClick={() => setOpenFaq(openFaq === 11 ? null : 11)}>
              Kurz zusammengefasst: Du buchst Mind Your Nature über die Plattform. Du buchst deine Übernachtung direkt bei der Location. Du kümmerst dich eigenständig um deine Verpflegung, mit der Option auf gemeinsames Kochen und Essen.
            </AccordionItem>
          </div>
        </section>

        {/* CTA Section - BOOKING */}
        <section className="py-32 border-t border-stone-50">
          <div className="space-y-16">
            <div className="text-center space-y-8">
              <h2 className="text-3xl md:text-4xl font-light text-stone-900">
                Bereit für diesen Raum?
              </h2>
              <p className="text-lg text-stone-500 font-light max-w-2xl mx-auto">
                Sicher dir deinen Platz und beginne deine Reise zur Wahlfreiheit.
              </p>
            </div>

            <motion.a
              href="https://www.eventbrite.de/o/mind-your-nature-120905315027"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="block w-full md:w-auto md:mx-auto"
            >
              <div className="bg-[#79836a] hover:bg-[#6a7359] text-white py-8 px-12 rounded-sm flex items-center justify-center gap-4 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer">
                <span className="text-xl font-light tracking-tight">Jetzt einen Platz sichern</span>
                <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
              </div>
            </motion.a>

            <div className="text-center space-y-6 pt-8">
              <div className="space-y-4">
                <p className="text-stone-500 font-light text-sm">Fragen zum Format?</p>
                <a 
                  href="mailto:mike@underopensky.de" 
                  className="inline-block text-moss-600 hover:text-moss-700 font-light tracking-tight transition-colors text-lg"
                >
                  mike@underopensky.de
                </a>
              </div>

              <motion.a
                href="https://youtu.be/M6P-5wtyZDk"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="block w-full md:w-auto md:mx-auto"
              >
                <div className="bg-[#79836a] hover:bg-[#6a7359] text-white py-8 px-12 rounded-sm flex items-center justify-center gap-4 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer">
                  <span className="text-xl font-light tracking-tight">Zum Info-Video</span>
                  <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
                </div>
              </motion.a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-20 border-t border-stone-50">
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex flex-wrap gap-10 text-[10px] text-stone-400 font-light uppercase tracking-[0.2em]">
                <button onClick={() => navigate('/impressum')} className="hover:text-moss-700 transition-colors">Impressum</button>
                <button onClick={() => navigate('/datenschutz')} className="hover:text-moss-700 transition-colors">Datenschutz</button>
                <button onClick={() => navigate('/agb')} className="hover:text-moss-700 transition-colors">AGB & Stornierung</button>
              </div>
              <motion.a
                href="https://www.eventbrite.de/o/mind-your-nature-120905315027"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 bg-[#79836a] hover:bg-[#6a7359] text-white text-xs font-light px-6 py-3 rounded-full transition-colors w-fit"
              >
                Buchen
                <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
              </motion.a>
            </div>
            <div className="text-stone-300 italic text-xs">© 2026 Mind Your Nature</div>
          </div>
        </footer>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {activeModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-md" onClick={closeModal} />
            <motion.div
              className={`relative bg-white shadow-2xl w-full ${activeModal === 'reflection' || activeModal === 'expectations' ? 'max-w-md' : 'max-w-3xl'} overflow-y-auto max-h-[90vh] rounded-sm`}
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
            >
              <div className="p-10 md:p-14">
                <div className="flex justify-between items-start mb-10">
                  <h4 className="text-lg font-light text-stone-900 tracking-tight">
                    {activeModal === 'reflection' && 'Kennst du das?'}
                    {activeModal === 'expectations' && 'Was dich erwartet'}
                  </h4>
                  <button onClick={closeModal} className="p-2 -mr-2 hover:bg-stone-50 rounded-full transition-colors group">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-stone-300 group-hover:text-stone-600" />
                  </button>
                </div>

                <div className="text-stone-600 font-light leading-relaxed text-sm">
                  {activeModal === 'expectations' && (
                    <div className="space-y-8">
                      <div className="space-y-4">
                        <p className="text-moss-700 font-medium">Der Rahmen & die Begleitung</p>
                        <p>Zwei Menschen gestalten diesen Rahmen achtsam und stehen dir während der gesamten Zeit für Fragen und als präsente Ansprechpartner zur Verfügung.</p>
                      </div>
                      <div className="space-y-4">
                        <p className="text-moss-700 font-medium">Das Ankommen</p>
                        <p>Wir beginnen gemeinsam mit gezielten Entspannungsübungen in der Natur, um das Nervensystem zu regulieren und den Übergang in die Stille zu erleichtern.</p>
                      </div>
                      <div className="space-y-4">
                        <p className="text-moss-700 font-medium">Minimale Impulse</p>
                        <p>Sollten deine eigenen Impulse einmal zu schwach sein oder die Orientierung im Raum schwerfallen, bieten wir minimale, sanfte Anleitung an – immer mit dem Ziel, deine Eigenverantwortung zu stützen.</p>
                      </div>
                    </div>
                  )}

                  {activeModal === 'reflection' && (
                    <div className="space-y-8">
                      <p className="text-stone-500 italic">Manchmal fühlen wir uns im eigenen Leben wie ein Passagier auf dem Autopiloten.</p>
                      <ul className="space-y-6">
                        <li className="flex gap-4">
                          <span className="w-1.5 h-1.5 rounded-full bg-moss-200 mt-2 shrink-0"></span>
                          <span>Der Gedanke „Ich könnte einen Fehler machen" lähmt dich.</span>
                        </li>
                        <li className="flex gap-4">
                          <span className="w-1.5 h-1.5 rounded-full bg-moss-200 mt-2 shrink-0"></span>
                          <span>Du reagierst gereizt, obwohl du ruhig bleiben wolltest.</span>
                        </li>
                        <li className="flex gap-4">
                          <span className="w-1.5 h-1.5 rounded-full bg-moss-200 mt-2 shrink-0"></span>
                          <span>Du grübelst über Situationen, die du längst nicht ändern kannst.</span>
                        </li>
                      </ul>
                    </div>
                  )}

                  <div className="pt-12">
                    <button onClick={closeModal} className="text-[10px] uppercase tracking-[0.2em] text-stone-400 hover:text-stone-900 transition-colors">Schließen</button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Home;